/**
 * Rebuy Smart Cart integration (global).
 * - Opens the Rebuy Smart Cart when the header cart icon or any /cart link is clicked.
 * - Opens the Smart Cart after a Dawn product-form add to cart.
 * - Opens (or closes) the Smart Cart from the Octane quiz "View cart" button
 *   instead of going to the cart page.
 * - Refreshes the header cart icon and count bubble on Rebuy cart events.
 * Depends on constants.js, pubsub.js and the `routes` global from theme.liquid.
 */

(function () {
  const REBUY_READY_TIMEOUT_MS = 2500;
  const REBUY_CART_REFRESH_TIMEOUT_MS = 1500;
  const CART_BUBBLE_DEBOUNCE_MS = 300;
  let cartBubbleTimeout;

  function cartPageUrl() {
    return window.routes?.cart_url || '/cart';
  }

  function isCartPageLink(anchor) {
    if (!anchor?.href) return false;

    try {
      const hrefUrl = new URL(anchor.href, window.location.origin);
      const cartUrl = new URL(cartPageUrl(), window.location.origin);
      return hrefUrl.pathname.replace(/\/$/, '') === cartUrl.pathname.replace(/\/$/, '');
    } catch {
      return false;
    }
  }

  function showRebuySmartCart(event) {
    if (event) {
      event.preventDefault();
    }

    if (window.Rebuy?.SmartCart?.show) {
      window.Rebuy.SmartCart.show();
      return;
    }

    const onReady = () => {
      window.clearTimeout(fallbackTimer);
      window.Rebuy?.SmartCart?.show?.();
    };

    const fallbackTimer = window.setTimeout(() => {
      document.removeEventListener('rebuy:smartcart.ready', onReady);
      if (!window.Rebuy?.SmartCart?.show) {
        window.location.href = cartPageUrl();
      }
    }, REBUY_READY_TIMEOUT_MS);

    document.addEventListener('rebuy:smartcart.ready', onReady, { once: true });
  }

  // The product form adds to cart directly, so Rebuy still holds the old cart
  // and would open empty. Rebuy.Cart.fetchCart(callback) is callback-based and
  // returns false when Rebuy is mid-update; the timeout caps the wait.
  function refreshRebuyCart(callback) {
    const fetchCart = window.Rebuy?.Cart?.fetchCart;
    if (typeof fetchCart !== 'function') {
      callback();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timeoutTimer);
      callback();
    };
    const timeoutTimer = window.setTimeout(finish, REBUY_CART_REFRESH_TIMEOUT_MS);

    try {
      if (fetchCart.call(window.Rebuy.Cart, finish) === false) finish();
    } catch {
      finish();
    }
  }

  function isSmartCartOpen() {
    return (
      document.body.classList.contains('rebuy-cart-visible') ||
      document.documentElement.classList.contains('rebuy-cart-visible')
    );
  }

  // The Octane quiz turns its "Add to cart" button into "View cart", which
  // sends the shopper to the cart page. Open the Smart Cart instead, or close
  // it when it is already open.
  function isQuizViewCartButton(button) {
    if (!button?.classList.contains('oct-quiz-btn')) return false;
    const label = (button.title || button.textContent || '').trim().toLowerCase();
    return label === 'view cart';
  }

  function toggleSmartCartFromQuiz(event) {
    // Stop Octane's own click handler, which navigates to /cart.
    event.preventDefault();
    event.stopPropagation();

    if (isSmartCartOpen() && window.Rebuy?.SmartCart?.hide) {
      window.Rebuy.SmartCart.hide();
      return;
    }

    showRebuySmartCart();
  }

  function refreshCartBubble() {
    clearTimeout(cartBubbleTimeout);
    cartBubbleTimeout = setTimeout(() => {
      const cartLink = document.getElementById('cart-icon-bubble');
      if (!cartLink) return;

      fetch('/?sections=cart-icon-bubble')
        .then((response) => response.json())
        .then((data) => {
          const html = data['cart-icon-bubble'];
          if (!html) return;

          const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          if (section) cartLink.innerHTML = section.innerHTML;
        })
        .catch((error) => console.error('[cart-bubble]', error));
    }, CART_BUBBLE_DEBOUNCE_MS);
  }

  function onDocumentClick(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    const quizButton = event.target.closest('button.oct-quiz-btn');
    if (isQuizViewCartButton(quizButton)) {
      toggleSmartCartFromQuiz(event);
      return;
    }

    const cartIcon = event.target.closest('#cart-icon-bubble, .header__icon--cart');
    if (cartIcon) {
      showRebuySmartCart(event);
      return;
    }

    const anchor = event.target.closest('a[href]');
    if (anchor && isCartPageLink(anchor)) {
      showRebuySmartCart(event);
    }
  }

  document.addEventListener('click', onDocumentClick, true);

  [
    'rebuy:cart.add',
    'rebuy:cart.change',
    'rebuy:smartcart.line-item-removed',
    'rebuy:smartcart.line-item-decrease',
    'rebuy:smartcart.line-item-increase',
  ].forEach((eventName) => {
    document.addEventListener(eventName, refreshCartBubble);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'product-form') {
        refreshRebuyCart(showRebuySmartCart);
      }
      refreshCartBubble();
    });
  }

  window.showRebuySmartCart = showRebuySmartCart;
})();
